import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export interface SelectedResult {
  id: string;
  configId: string;
  configName: string;
  sheetJson: object | null;
}

export interface SelectedBattle {
  id: string; // vote_id
  arenaRunId: string;
  promptText: string;
  promptId: string | null;
  category: string | null;
  source: 'seed' | 'arena' | null;
  resultA: SelectedResult;
  resultB: SelectedResult;
  winner: 'a' | 'b' | 'tie' | 'both_bad';
  selectedAt: string;
}

interface SelectionContextType {
  selections: SelectedBattle[];
  selectionCount: number;
  addSelection: (battle: Omit<SelectedBattle, 'selectedAt'>) => void;
  removeSelection: (id: string) => void;
  clearSelections: () => void;
  isSelected: (id: string) => boolean;
  toggleSelection: (battle: Omit<SelectedBattle, 'selectedAt'>) => void;
}

const STORAGE_KEY = 'spreadsheet-study-selections';

const SelectionContext = createContext<SelectionContextType | null>(null);

function loadSelectionsFromStorage(): SelectedBattle[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) {
    console.error('Failed to load selections from storage:', e);
  }
  return [];
}

function saveSelectionsToStorage(selections: SelectedBattle[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selections));
  } catch (e) {
    console.error('Failed to save selections to storage:', e);
  }
}

export function SelectionProvider({ children }: { children: ReactNode }) {
  const [selections, setSelections] = useState<SelectedBattle[]>(() =>
    loadSelectionsFromStorage()
  );

  useEffect(() => {
    saveSelectionsToStorage(selections);
  }, [selections]);

  const addSelection = useCallback(
    (battle: Omit<SelectedBattle, 'selectedAt'>) => {
      setSelections(prev => {
        // Check if already selected
        if (prev.some(s => s.id === battle.id)) {
          return prev;
        }
        return [
          ...prev,
          {
            ...battle,
            selectedAt: new Date().toISOString(),
          },
        ];
      });
    },
    []
  );

  const removeSelection = useCallback((id: string) => {
    setSelections(prev => prev.filter(s => s.id !== id));
  }, []);

  const clearSelections = useCallback(() => {
    setSelections([]);
  }, []);

  const isSelected = useCallback(
    (id: string) => {
      return selections.some(s => s.id === id);
    },
    [selections]
  );

  const toggleSelection = useCallback(
    (battle: Omit<SelectedBattle, 'selectedAt'>) => {
      if (isSelected(battle.id)) {
        removeSelection(battle.id);
      } else {
        addSelection(battle);
      }
    },
    [isSelected, removeSelection, addSelection]
  );

  const value = useMemo(
    () => ({
      selections,
      selectionCount: selections.length,
      addSelection,
      removeSelection,
      clearSelections,
      isSelected,
      toggleSelection,
    }),
    [
      selections,
      addSelection,
      removeSelection,
      clearSelections,
      isSelected,
      toggleSelection,
    ]
  );

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
}

export function useSelection() {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error('useSelection must be used within a SelectionProvider');
  }
  return context;
}

export function useSelectionOptional() {
  return useContext(SelectionContext);
}
